<template>
  <div class="keno-page">
    <h2 v-if="teaser" class="headline-h4-headline mb-100">
      {{ $t('highfrequencygame.hub.headline') }}
    </h2>

    <NuxtLayout name="teasers" v-bind="{ loading }" :no-results="!teaser" @refresh="kenoRefetch">
      <HighFrequencyGameTeaser
        v-if="teaser"
        v-bind="{
          ...teaser,
          licensedTerritory,
        }"
        @click="handleTeaserClick(teaser)"
      />

      <HighFrequencyGameTeaserWatch
        v-if="teaser && gameNumber && gameId"
        class="teaser-watch"
        :id="gameId"
        v-bind="{ licensedTerritory, gameNumber }"
        :is-operational="teaser.operating"
        :operating-hours="teaser.operatingHours"
      />

      <template #no-results>
        <LazyNoResults
          :alt="$t('home.tab.keno.noresults.alt')"
          :headline="$t('home.tab.keno.noresults.headline')"
          :subline="$t('home.tab.keno.noresults.subline')"
          illustration="no-keno"
          variant="outlined"
        />
      </template>
    </NuxtLayout>

    <template v-if="teaser">
      <HighFrequencyGameWinnersCard v-if="winnersCount" :count="winnersCount" class="mb-200" />

      <div class="flex flex-col gap-100 pb-200">
        <h2 class="headline-h4-headline">
          {{ $t('highfrequencygame.hub.results.headline') }}
        </h2>

        <LcSkeleton v-if="showResultsSkeleton" height="30rem" />

        <HighFrequencyGameResultsCard
          v-else-if="gameId"
          v-bind="{
            gameId,
            canNavigateOlder,
            canNavigateNewer,
            isGameNotFound,
            isSearching,
            hasNewGame,
            isLatest,
          }"
          :draws="drawsOldestFirst"
          :current-draw-number="currentDrawNumber ?? undefined"
          @change="handleChange"
          @search="handleSearch"
          @new-game="handleNewGame"
          @play-now="handlePlayNow"
        />
      </div>

      <HighFrequencyGameOnboardingCarousel v-if="gameConfig" :config="gameConfig" />
    </template>

    <HighFrequencyGameExploreMoreCarousel :variants="['for-you', 'draw', 'scratch']" />
  </div>
</template>

<script lang="ts" setup>
import {
  KnownProductType,
  ProductCatalogElement,
  type HighFrequencyTeaserPayload,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import HighFrequencyGameResultsCard from '#high-frequency-game/app/components/HighFrequencyGameResultsCard.vue'
import HighFrequencyGameTeaser from '#high-frequency-game/app/components/HighFrequencyGameTeaser.vue'
import { useKenoHub } from '#high-frequency-game/app/composables/kenoHub'

import { useFetchOptions } from '../utils/fetchOptions'

const isHighFrequencyTeaser = (element: {
  __typename?: string
}): element is HighFrequencyTeaserPayload => element.__typename === 'HighFrequencyTeaserPayload'

const licensedTerritory = useLicensedTerritory({ raw: true })
const gameId = useHighFrequencyGameId()

const {
  loading,
  refetch: kenoRefetch,
  result: kenoResult,
} = useProductCatalogQuery(
  { licensedTerritory, productType: KnownProductType.HighFrequencyGame },
  useFetchOptions(),
)

const {
  canNavigateNewer,
  canNavigateOlder,
  currentDrawNumber,
  draws,
  gameConfig,
  handleChange,
  handleNewGame,
  handlePlayNow,
  handleSearch,
  hasNewGame,
  isGameNotFound,
  isLatest,
  isSearching,
  latestDrawNumber,
  loading: resultsLoading,
  newDrawNumber,
  winnersCount,
} = useKenoHub()

const drawsOldestFirst = computed(() => [...draws.value].reverse())

const teasers = computed(() => {
  const result = kenoResult.value?.productCatalog
  if (!isGraphqlSuccess(result)) return []
  return result.elements.filter(isHighFrequencyTeaser) as HighFrequencyTeaserPayload[]
})

const teaser = computed(() => teasers.value[0])

const gameNumber = computed(() => newDrawNumber.value || latestDrawNumber.value)

const showResultsSkeleton = computed(
  () =>
    (loading.value && resultsLoading.value) ||
    (!currentDrawNumber.value && !isSearching.value && !isGameNotFound.value),
)

const TRACKING_LIST_NAME = 'Home Keno Teasers'
const TRACKING_LIST_ID = 'home_keno_teasers'

const { createTeaserListItems } = useTrackingData()
const { pushSelectListItemEvent, pushViewListEvent } = useTracking()
const { logTrackingError } = useTrackingErrorLog()

const trackingTeaserListItems = ref<ReturnType<typeof createTeaserListItems>>([])

const handleTeaserClick = (teaser: HighFrequencyTeaserPayload) => {
  const teaserItem = trackingTeaserListItems.value.find((item) => item.item_id === teaser.id)

  if (!teaserItem) return

  try {
    pushSelectListItemEvent({
      items: [teaserItem],
      list_id: TRACKING_LIST_ID,
      list_name: TRACKING_LIST_NAME,
    })
  } catch (err) {
    logTrackingError('select_list_item', err)
  }
}

let unwatchTeasers: Maybe<() => void> = null
unwatchTeasers = watchImmediate(teasers, (list) => {
  if (!list || list.length === 0) return

  try {
    trackingTeaserListItems.value = createTeaserListItems(list)
    pushViewListEvent({
      items: trackingTeaserListItems.value,
      list_id: TRACKING_LIST_ID,
      list_name: TRACKING_LIST_NAME,
    })

    if (unwatchTeasers) unwatchTeasers()
  } catch (err) {
    logTrackingError('view_list', err)
  }
})
</script>

<style lang="scss" scoped>
.keno-page :deep(.teasers) {
  gap: token('size/space/gap/200');
  padding-bottom: token('size/space/gap/200');
}
</style>
